import { Controller } from "@hotwired/stimulus";

// Collapses a long property description to the height available beside its
// overview photo, while leaving the full MLS copy in the DOM. With JavaScript
// unavailable the description remains fully readable (progressive enhancement).
export default class extends Controller {
  static targets = ["content", "reference", "toggle"];

  connect() {
    this.expanded = false;
    this.measure = this.measure.bind(this);
    window.addEventListener("resize", this.measure);

    if (this.hasReferenceTarget && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.referenceTarget);
    }

    this.measure();
  }

  disconnect() {
    window.removeEventListener("resize", this.measure);
    this.resizeObserver?.disconnect();
  }

  toggle() {
    this.expanded = !this.expanded;
    this.applyState();
  }

  measure() {
    if (!this.hasContentTarget || !this.hasToggleTarget) return;

    // Measure the natural content height, not a previous collapsed height.
    this.contentTarget.style.maxHeight = "";
    this.contentTarget.classList.remove("is-collapsed");
    this.collapsedHeight = this.availableHeight();
    this.overflows = this.contentTarget.scrollHeight > this.collapsedHeight + 1;

    if (!this.overflows) this.expanded = false;
    this.applyState();
  }

  availableHeight() {
    if (!this.hasReferenceTarget) return 320;

    const reference = this.referenceTarget.getBoundingClientRect();
    const content = this.contentTarget.getBoundingClientRect();
    const desktop = window.matchMedia?.("(min-width: 860px)").matches ?? window.innerWidth >= 860;
    // On desktop, align the Read more control with the photo's bottom edge.
    // Stacked mobile layouts use the photo's own height as the copy allowance.
    const available = desktop ? reference.bottom - content.top : reference.height;
    return Math.max(128, Math.floor(available - 36));
  }

  applyState() {
    this.toggleTarget.hidden = !this.overflows;
    this.toggleTarget.setAttribute("aria-expanded", String(this.expanded));
    this.toggleTarget.textContent = this.expanded ? "Read less" : "Read more";

    const collapsed = this.overflows && !this.expanded;
    this.contentTarget.classList.toggle("is-collapsed", collapsed);
    this.contentTarget.style.maxHeight = collapsed ? `${this.collapsedHeight}px` : "";
  }
}
